/**
 * Subscriber
 * @description 購読・発行の機能を提供する
 * @requires jquery
 */
export default class Subscriber {
  listeners: Object;

  // constructor -----------------------------------------------------------------------------------

  /**
   * コンストラクタ
   * @constructs
   */
  constructor() {
    this.listeners = this.listeners || {};
  }

  // static ----------------------------------------------------------------------------------------

  /**
   * スペース区切りのイベント名を配列形式に変換する
   * @param {string} eventNames イベント名（スペース区切りで複数指定可）
   * @returns {Array}
   * @static
   */
  static _splitEventNames(eventNames: string): string {
    return eventNames.trim().replace(/\s+/, ' ').split(' ');
  }

  // private ---------------------------------------------------------------------------------------

  /**
   * 指定のイベント名のイベントリスナが登録されているか
   * @param {string} eventName イベント名
   * @returns {boolean}
   * @private
   */
  _hasListener(eventName: string): boolean {
    if (eventName in this.listeners === false) {
      return false;
    } else if (!this.listeners[eventName].length) {
      return false;
    }

    return true;
  }

  /**
   * イベントリスナを登録する
   * @param {string} eventName イベント名
   * @param {Function} handler コールバック関数
   * @private
   */
  _addListener(eventName: string, handler: Function): Subscriber {
    if (eventName in this.listeners === false) {
      this.listeners[eventName] = [];
    }

    this.listeners[eventName].push(handler);
  }

  /**
   * イベントリスナを削除する
   * @param {string} eventName イベント名
   * @private
   */
  _removeListener(eventName: string): Subscriber {
    delete this.listeners[eventName];
  }

  // public ----------------------------------------------------------------------------------------

  /**
   * イベントリスナを登録する
   * @param {string} eventNames イベント名（スペース区切りで複数指定可）
   * @param {Function} handler コールバック関数
   * @returns {Class} 自身のインスタンス
   * @public
   */
  on(eventNames: string, handler: Function): Subscriber {
    this.constructor._splitEventNames(eventNames).forEach((eventName) => {
      this._addListener(eventName, handler);
    });

    return this;
  }

  /**
   * イベントリスナを削除する
   * @param {string} eventNames イベント名（スペース区切りで複数指定可）
   * @returns {Class} 自身のインスタンス
   * @public
   */
  off(eventNames: string): Subscriber {
    this.constructor._splitEventNames(eventNames).forEach((eventName) => {
      this._removeListener(eventName);
    });

    return this;
  }

  /**
   * 指定のイベントを発火させる
   * @param {string} eventNames イベント名（スペース区切りで複数指定可）
   * @returns {Class} 自身のインスタンス
   * @public
   */
  trigger(eventNames: string): Subscriber {
    this.constructor._splitEventNames(eventNames).forEach((eventName) => {
      if (this._hasListener(eventName)) {
        this.listeners[eventName].forEach((handler) => {
          handler();
        });
      }
    });

    return this;
  }
}
